import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';

const LinkedinIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>);
const TwitterIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>);
const InstagramIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>);
const GithubIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>);

const contactInfo = [
  {
    icon:  <Mail size={20} />,
    label: 'Email Us',
    value: 'qubrightoffice@gmail.com',
    link:  'mailto:qubrightoffice@gmail.com',
    color: '#1A3BCC',
    light: '#EEF2FF',
  },
  {
    icon:  <Phone size={20} />,
    label: 'Call Us',
    value: '+91 7676654809',
    link:  'tel:+917676654809',
    color: '#FF7B00',
    light: '#FFF7ED',
  },
  {
    icon:  <MapPin size={20} />,
    label: 'Visit Us',
    value: 'India — Serving Globally',
    link:  '#',
    color: '#0EA5E9',
    light: '#F0F9FF',
  },
];

const socials = [
  { icon: <LinkedinIcon />, href: '#', label: 'LinkedIn' },
  { icon: <TwitterIcon />,  href: '#', label: 'Twitter' },
  { icon: <InstagramIcon />, href: '#', label: 'Instagram' },
  { icon: <GithubIcon />,   href: '#', label: 'GitHub' },
];

export default function Contact() {
  const [titleRef, titleInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [form, setForm] = useState({ name: '', email: '', company: '', service: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'contacts'), {
        ...form,
        createdAt: new Date()
      });
      setSent(true);
      setForm({ name: '', email: '', company: '', service: '', message: '' });
      setTimeout(() => setSent(false), 4000);
    } catch (error) {
      console.error("Error adding document: ", error);
      alert("Something went wrong. Please try again later.");
    }
  };

  return (
    <section id="contact" className="py-28 bg-brand-light relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
      <div className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full bg-brand-blue/4 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-brand-orange/5 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          ref={titleRef}
          initial={{ y: 30, opacity: 0 }}
          animate={titleInView ? { y: 0, opacity: 1 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="section-label mb-5 inline-block">Get In Touch</span>
          <h2 className="font-syne font-extrabold text-4xl md:text-5xl text-slate-900 mb-5">
            Let's Build <span className="gradient-text">Together</span>
          </h2>
          <p className="font-inter text-slate-500 text-lg max-w-xl mx-auto">
            Have a project in mind? Looking for training? Want to partner with us?
            We'd love to hear from you.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-10">
          {/* LEFT — Info */}
          <motion.div
            initial={{ x: -40, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-2 flex flex-col gap-6"
          >
            {/* Contact cards */}
            {contactInfo.map((info, i) => (
              <a key={i} href={info.link} className="group">
                <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-card flex items-center gap-4 hover:shadow-royal transition-all duration-300 hover:-translate-y-0.5">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300"
                    style={{ background: info.light, color: info.color }}
                  >
                    {info.icon}
                  </div>
                  <div>
                    <p className="font-space text-xs text-slate-400 font-medium tracking-wider uppercase mb-0.5">{info.label}</p>
                    <p className="font-inter font-semibold text-slate-800 text-sm">{info.value}</p>
                  </div>
                </div>
              </a>
            ))}

            {/* Tagline card */}
            <div className="bg-gradient-to-br from-brand-blue to-blue-700 rounded-2xl p-6 text-white mt-2">
              <p className="font-syne font-bold text-lg mb-1">QuBright Technologies LLP</p>
              <p className="font-space text-blue-200 text-sm italic mb-4">
                "Where Creativity Meets Precision"
              </p>
              <p className="font-inter text-blue-200 text-sm">
                We respond to every inquiry within 24 hours. Your vision is our mission.
              </p>
              {/* Socials */}
              <div className="flex gap-3 mt-5">
                {socials.map((s, i) => (
                  <a
                    key={i}
                    href={s.href}
                    aria-label={s.label}
                    className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors duration-200"
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* RIGHT — Form */}
          <motion.div
            initial={{ x: 40, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-3"
          >
            <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-card">
              <h3 className="font-syne font-bold text-2xl text-slate-900 mb-6">Send us a Message</h3>

              {sent ? (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="py-16 flex flex-col items-center justify-center text-center"
                >
                  <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-4">
                    <Send size={24} className="text-green-500" />
                  </div>
                  <h4 className="font-syne font-bold text-xl text-slate-900 mb-2">Message Sent!</h4>
                  <p className="font-inter text-slate-500 text-sm">We'll get back to you within 24 hours.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="font-space text-xs font-semibold text-slate-600 mb-1.5 block tracking-wider uppercase">Full Name *</label>
                      <input
                        className="form-input"
                        name="name"
                        placeholder="John Doe"
                        value={form.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <label className="font-space text-xs font-semibold text-slate-600 mb-1.5 block tracking-wider uppercase">Email *</label>
                      <input
                        className="form-input"
                        name="email"
                        type="email"
                        placeholder="john@company.com"
                        value={form.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="font-space text-xs font-semibold text-slate-600 mb-1.5 block tracking-wider uppercase">Company</label>
                      <input
                        className="form-input"
                        name="company"
                        placeholder="Your Company"
                        value={form.company}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <label className="font-space text-xs font-semibold text-slate-600 mb-1.5 block tracking-wider uppercase">Service Interested In</label>
                      <select
                        className="form-input"
                        name="service"
                        value={form.service}
                        onChange={handleChange}
                      >
                        <option value="">Select a service</option>
                        <option>AI & Machine Learning</option>
                        <option>Mobile App Development</option>
                        <option>Web Development</option>
                        <option>Cloud & DevOps</option>
                        <option>UI/UX Design</option>
                        <option>Training / SDP / FDP</option>
                        <option>Internship Programme</option>
                        <option>College Partnership</option>
                        <option>Product Demo</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="font-space text-xs font-semibold text-slate-600 mb-1.5 block tracking-wider uppercase">Message *</label>
                    <textarea
                      className="form-input resize-none"
                      name="message"
                      rows={5}
                      placeholder="Tell us about your project, requirements, or any questions..."
                      value={form.message}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <button type="submit" className="btn-primary flex items-center justify-center gap-2 mt-2">
                    <Send size={16} />
                    Send Message
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
